import { useEffect, useRef } from "react";
import { CHAT_CAMPAIGN_OPEN_AUTO_SUMMARY_QUESTION } from "../../../i18n/keysTranslations";

/** Mirrors CommonChatContainer report.states preload (unapplied optimizations). */
export const hasCampaignReportStatesChatPreload = (reportStates = {}) => {
  if (!reportStates || Object.keys(reportStates).length === 0) {
    return false;
  }

  return Object.values(reportStates).some((data) => {
    const optimizations = data?.optimizations || [];
    if (optimizations.length === 0) {
      return false;
    }
    return !optimizations.every((optimization) => optimization.isApplied);
  });
};

export const isCampaignOpeningAssistEligible = ({
  campaignId,
  chatMessages,
  reportStates,
}) =>
  Boolean(campaignId) &&
  Object.keys(chatMessages || {}).length === 0 &&
  !hasCampaignReportStatesChatPreload(reportStates);

/**
 * Auto-sends the campaign opening summary prompt when the embedded chat is empty
 * and the campaign has finished syncing.
 */
export const useCampaignOpeningAutoPrompt = ({
  campaignId,
  chatMessages,
  reportStates,
  isSyncingData,
  chatActionsRef,
  pendingOpeningHighlightRef,
  t,
}) => {
  /** Once per campaign visit; chat reset must not re-trigger the opening prompt. */
  const autoPromptSentForCampaignIdRef = useRef(null);

  const isEligible = isCampaignOpeningAssistEligible({
    campaignId,
    chatMessages,
    reportStates,
  });

  const canAutoSend =
    isEligible &&
    !isSyncingData &&
    typeof chatActionsRef.current?.triggerHiddenUserTurn === "function";

  const openingAssistConsumed =
    Boolean(campaignId) &&
    autoPromptSentForCampaignIdRef.current === campaignId;

  useEffect(() => {
    if (!canAutoSend) {
      return undefined;
    }

    if (autoPromptSentForCampaignIdRef.current === campaignId) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const triggerHiddenUserTurn =
        chatActionsRef.current?.triggerHiddenUserTurn;
      if (!triggerHiddenUserTurn) {
        return;
      }

      const text = t(CHAT_CAMPAIGN_OPEN_AUTO_SUMMARY_QUESTION);
      const accepted = await Promise.resolve(triggerHiddenUserTurn(text));
      if (cancelled || !accepted) {
        return;
      }
      autoPromptSentForCampaignIdRef.current = campaignId;
      if (pendingOpeningHighlightRef) {
        pendingOpeningHighlightRef.current = true;
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chatActionsRef is stable ref object
  }, [canAutoSend, campaignId, chatMessages, reportStates, t]);

  return { openingAssistConsumed };
};
